import { mapHrefForPlace } from '@features/map/focus'
import { POI_ICONS } from '@features/map/markers'
import { useStages } from '@features/stages/useStages'
import { useI18n } from '@i18n/I18nProvider'
import { directionsUrl } from '@lib/directions'
import { ButtonLink, buttonClasses } from '@ui/Button'
import { Icon } from '@ui/Icon'
import { PhotoGallery } from '@ui/PhotoGallery'
import { Screen } from '@ui/Screen'
import { ErrorState, LoadingState } from '@ui/States'
import { Link } from 'wouter'
import { findPlace, useLocalities, useMunicipalities, usePlaces } from './usePlaces'

// Ficha de un lugar de interés: fotos, categoría, localidad (o municipio si está fuera de un pueblo), descripción, "Ver en el mapa",
// "Cómo llegar" y la etapa en la que está. Se llega desde la ficha de etapa o desde el mapa.
export const PlacePage = ({ id }: { id: string }) => {
	const { t } = useI18n()
	const { data, error, isPending, refetch } = usePlaces()
	const municipalities = useMunicipalities()
	const localities = useLocalities()
	const stages = useStages()

	if (isPending)
		return (
			<Screen>
				<LoadingState shape="detail" />
			</Screen>
		)
	if (error || !data) return <ErrorState error={error} onRetry={() => refetch()} />
	const place = findPlace(data.items, id)
	if (!place)
		return (
			<Screen backHref="/">
				<div role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
					<Icon name="pin" className="size-12 text-earth-600" />
					<p className="text-lg">{t('places.notFound')}</p>
				</div>
			</Screen>
		)

	// Localidad, municipio y etapa son complementos: si no cargan, la ficha se ve igual sin ellos.
	const locality = place.localityId ? localities.data?.items.find((l) => l.id === place.localityId) : undefined
	const municipality = municipalities.data?.items.find((m) => m.id === place.municipalityId)
	const where = locality?.name ?? municipality?.name
	const stage = place.stageId ? stages.data?.items.find((s) => s.id === place.stageId) : undefined
	const directions = directionsUrl(place.location)

	return (
		<Screen backHref={stage ? `/etapas/${encodeURIComponent(stage.id)}` : '/'}>
			<PhotoGallery photos={place.photoUrls} alt={place.name} />
			<p className="flex items-center gap-2 font-semibold text-earth-800">
				<Icon name={POI_ICONS[place.category]} className="size-5" />
				{t(`poi.${place.category}`)}
				{where && <span>· {where}</span>}
			</p>
			<h1 className="mb-3 text-3xl leading-tight">{place.name}</h1>
			<p className="mb-5 text-lg">{place.description}</p>

			<div className="flex flex-wrap gap-3">
				<ButtonLink href={mapHrefForPlace(place.id)}>
					<Icon name="map" />
					{t('places.viewOnMap')}
				</ButtonLink>
				{directions && (
					<a
						href={directions}
						target="_blank"
						rel="noopener noreferrer"
						className={buttonClasses('secondary')}
					>
						<Icon name="route" />
						{t('places.directions')}
						<Icon name="external" className="size-5" />
					</a>
				)}
			</div>

			{stage && (
				<Link
					href={`/etapas/${encodeURIComponent(stage.id)}`}
					className="mt-6 flex items-center gap-3 rounded-3xl bg-white p-4 shadow-sm active:bg-earth-100"
				>
					<div className="min-w-0 flex-1">
						<p className="text-sm font-semibold text-earth-800">
							{t('places.inStage', { n: stage.order })}
						</p>
						<p className="text-lg font-bold text-forest-800">{stage.name}</p>
					</div>
					<Icon name="forward" className="size-6 shrink-0 text-earth-600" />
				</Link>
			)}
		</Screen>
	)
}
